import { cx } from 'emotion';
import {
  bool, func, object, oneOf, string, number,
} from 'prop-types';
import React, { Component } from 'react';

import { DeviceInfo } from '@ezugi/utils';

import { createPortalComponent } from '../Portal/Portal';
import {
  StyledDescription, Container, LEAVE_TIME, notificationMobileClassName,
} from './style';

// We create a specific container (div#dialog-container) for all the dialog prompts.
const Portal = createPortalComponent('notification-container');
const POSITION_MAP = DeviceInfo.isHandheld ? {
  top: { top: '0' },
  center: { top: '50%', transform: 'translateY(-50%)' },
  bottom: { bottom: '0' },
} : {
  top: { top: '0', left: '50%', transform: 'translateX(-50%)' },
  center: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
  bottom: { bottom: '0', left: '50%', transform: 'translateX(-50%)' },
};

const DEFAULT_DISPLAY_TIME = 3000;
const DEFAULT_DISPLAY = false;

/**
 * Notification render-prop component.
 *
 * Usage:
 * <Notification
     style={}
     on={true}
     message={'Video not available'}
     align={'bottom'}
     delay={6000}
     onClose={handleClose}
   >
     {(props) => (
        // Use render prop if you want a specific layout
     )}
 * </Notification>
 *
 */
class Notification extends Component {

  constructor(props) {
    super(props);
    this.state = {
      show: props.on,
      position: this.getPosition(props.align),
    };
  }

  componentDidMount() {
    this.updatePosition(this.props.align);
    this.state.show && this.closeNotification();
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    this.clearTimeouts();

    this.togglePortal(nextProps.on);
    this.updatePosition(nextProps.align);

    nextProps.on && this.closeNotification(nextProps);
  }

  componentWillUnmount() {
    this.clearTimeouts();
  }

  getPosition = (align) => {
    if (this.props.getCustomPosition) {
      return this.props.getCustomPosition();
    }

    return POSITION_MAP[align];
  };

  updatePosition = (align) => {
    this.setState({
      position: this.getPosition(align),
    });
  };

  closeNotification = (props) => {
    const { delay: propsDelay, autoClose } = props || this.props;

    if (autoClose) {
      this.hideTimeout = setTimeout(() => {
        this.setState({
          style: { opacity: 0 },
        });
      }, propsDelay);

      // close the portal and remove the tooltip, but only after the specified time has passed.
      this.closeTimeout = setTimeout(() => {
        this.togglePortal(false);
      }, propsDelay + LEAVE_TIME * 1.5);
    }
  };

  showNotification = () => {
    this.updatePosition();
    this.togglePortal(true);
  };

  togglePortal = (show) => {
    const { onClose } = this.props;

    // on remove call callback prop onClose received from parent component
    !show && onClose && onClose();

    this.setState({
      show,
      style: {},
    });
  };

  clearTimeouts = () => {
    clearTimeout(this.closeTimeout);
    clearTimeout(this.hideTimeout);
  };

  renderNotification = () => {
    const {
      props: { style, className, message },
      state: { style: stateStyle, position },
    } = this;

    return (
      <Container
        className={cx({ [notificationMobileClassName]: DeviceInfo.isHandheld }, className)}
        style={{ ...position, ...style, ...stateStyle }}
      >
        {!!message && (
          <StyledDescription>
            {message}
          </StyledDescription>
        )}
      </Container>
    );
  };

  render() {
    const {
      props: {
        children,
        withPortal,
      },
      renderNotification,
      showNotification,
      closeNotification,
    } = this;

    if (!this.state.show) {
      return null;
    }

    return (
      withPortal ? (
        <Portal>
          {children ? (
            typeof children === 'function' ? children({ showNotification, closeNotification }) : children
          ) : (
            <>
              {renderNotification()}
            </>
          )}
        </Portal>
      ) : renderNotification()
    );
  }

}

Notification.propTypes = {
  on: bool,
  align: oneOf([ 'top', 'center', 'bottom' ]),
  style: object,
  message: string,
  // eslint-disable-next-line react/no-unused-prop-types
  delay: number,
  onClose: func,
  // eslint-disable-next-line react/no-unused-prop-types
  autoClose: bool,
  getCustomPosition: func,
  withPortal: bool,
};

Notification.defaultProps = {
  align: 'top',
  delay: DEFAULT_DISPLAY_TIME,
  on: DEFAULT_DISPLAY,
  autoClose: true,
  withPortal: true,
};

export default Notification;
